import React, { useMemo, useState } from 'react';
import {
  SlDialog,
  SlDialogHeader,
  SlDialogTitle,
  SlDialogCloseButton,
  SlDialogFooter,
  SlDialogContent,
  SlDialogProps
} from 'src/components/BeaconV2Components/SlDialog';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text, getTruncateStyles } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlLimitedInput } from 'src/components/BeaconV2Components/SlLimitedInput/SlLimitedInput';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { useAppSelector } from 'src/utils/Hooks';
import { SlCheckbox, SlCheckboxLabel, SlCheckboxContainer } from 'src/components/BeaconV2Components/SlCheckbox';
import { SlShowMore } from 'src/components/BeaconV2Components/SlShowMore/SlShowMore';
import {
  useAddTopicGroupStoreActions,
  useAddTopicGroupStoreState
} from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';

export interface AddTopicGroupModalProps extends SlDialogProps {
  handleNext: () => void;
}

export const AddTopicGroupModal = ({ onClose, handleNext, ...rest }: AddTopicGroupModalProps) => {
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategorySearch, setSubcategorySearch] = useState('');

  const { name, selectedCategoryIds, selectedSubcategoryIds } = useAddTopicGroupStoreState().topicGroupState;
  const { setTopicGroupState } = useAddTopicGroupStoreActions();
  const categories = useAppSelector((state) => state.categories.filter((category) => category.id !== 0));
  const subCategories = useAppSelector((state) => state.subCategories);

  const handleClearOptions = () => {
    setCategorySearch('');
    setSubcategorySearch('');
    setTopicGroupState(({ clear }) => clear());
  };

  const getAvailableSubcategories = () => {
    if (selectedCategoryIds.length === 0) {
      return subCategories;
    }

    return subCategories.filter((subcategory) => selectedCategoryIds.includes(subcategory.parentCategoryId));
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>, categoryId: number) => {
    const newSelectedCategories = e.target.checked
      ? [...selectedCategoryIds, categoryId]
      : selectedCategoryIds.filter((id) => id !== categoryId);

    setTopicGroupState(({ setCategoryIds }) => setCategoryIds(newSelectedCategories));

    // Filter out subcategories that don't belong to the selected categories
    if (newSelectedCategories.length > 0) {
      const availableSubcategoryIds = getAvailableSubcategories().map((subcategory) => subcategory.id);

      setTopicGroupState(({ setSubcategoryIds }) =>
        setSubcategoryIds(selectedSubcategoryIds.filter((id) => availableSubcategoryIds.includes(id)))
      );
    }
  };

  const handleSubcategoryChange = (e: React.ChangeEvent<HTMLInputElement>, subcategoryId: number) =>
    e.target.checked
      ? setTopicGroupState(({ setSubcategoryIds }) => setSubcategoryIds([...selectedSubcategoryIds, subcategoryId]))
      : setTopicGroupState(({ setSubcategoryIds }) =>
          setSubcategoryIds(selectedSubcategoryIds.filter((id) => id !== subcategoryId))
        );

  const isValid = useMemo(() => {
    return name.trim() && (selectedCategoryIds.length || selectedSubcategoryIds.length);
  }, [name, selectedCategoryIds, selectedSubcategoryIds]);

  return (
    <SlDialog
      width="752px"
      height="641px"
      onClose={(event, reason) => {
        handleClearOptions();
        // eslint-disable-next-line no-unused-expressions
        onClose?.(event, reason);
      }}
      {...rest}
    >
      <SlDialogHeader>
        <SlDialogTitle>Add Topic Group</SlDialogTitle>
        <SlDialogCloseButton onClick={(event) => onClose?.(event, 'backdropClick')} />
      </SlDialogHeader>
      <SlDialogContent
        sx={{
          paddingRight: '24px'
        }}
      >
        <Flex gap="sm" flexDirection="column">
          <Text variant="subtitle1">Create a Topic Group</Text>
          <Text variant="body2">Aggregate multiple topics by creating a topic group.</Text>
        </Flex>
        <Flex gap="sm" flexDirection="column" marginTop="24px">
          <Text variant="subtitle1">Name</Text>
          <SlLimitedInput
            value={name}
            onChange={(e) => setTopicGroupState(({ setName }) => setName(e.target.value))}
            placeholder="Name your topic group"
            characterLimit={100}
          />
        </Flex>
        <Flex flexDirection="column" marginTop="24px">
          <Text variant="subtitle1">Categories</Text>
          <SearchTextField
            placeholder="Search categories"
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            sx={{
              marginTop: '16px'
            }}
          />
          <SlShowMore
            items={categories
              .filter((category) => category.displayName?.toLowerCase().includes(categorySearch.toLowerCase()))
              .sort((cat) => {
                if (selectedCategoryIds.includes(cat.id)) {
                  return -1;
                }
                return 1;
              })}
            renderItems={(visibleCategories: { id: number; name: string }[]) => (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 190px)',
                  columnGap: '40px',
                  rowGap: '16px',
                  marginTop: '32px'
                }}
              >
                {visibleCategories
                  .sort((cat) => {
                    if (selectedCategoryIds.includes(cat.id)) {
                      return -1;
                    }
                    return 1;
                  })
                  .map((category) => (
                    <SlCheckboxContainer key={category.id}>
                      <SlCheckbox
                        onChange={(e) => handleCategoryChange(e, category.id)}
                        checked={selectedCategoryIds.includes(category.id)}
                        id={category.id.toString()}
                      />
                      <SlCheckboxLabel
                        style={{
                          ...(getTruncateStyles(1) as React.CSSProperties)
                        }}
                        htmlFor={category.id.toString()}
                        title={category.name}
                      >
                        {category.name}
                      </SlCheckboxLabel>
                    </SlCheckboxContainer>
                  ))}
              </div>
            )}
          />
        </Flex>
        <Flex flexDirection="column" marginTop="32px">
          <Text variant="subtitle1">Subcategories</Text>
          <SearchTextField
            placeholder="Search subcategories"
            value={subcategorySearch}
            onChange={(e) => setSubcategorySearch(e.target.value)}
            sx={{
              marginTop: '16px'
            }}
          />
          <SlShowMore
            items={getAvailableSubcategories()
              .filter((subcategory) => subcategory.displayName?.toLowerCase().includes(subcategorySearch.toLowerCase()))
              .sort((subcat) => {
                if (selectedSubcategoryIds.includes(Number(subcat?.id))) {
                  return -1;
                }
                return 1;
              })
              .map((subcat) => ({ ...subcat, id: Number(subcat.id) }))}
            renderItems={(visibleSubcategories: { id: number; name: string }[]) => (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 190px)',
                  columnGap: '40px',
                  rowGap: '16px',
                  marginTop: '32px'
                }}
              >
                {visibleSubcategories
                  .sort((subcat) => {
                    if (selectedSubcategoryIds.includes(subcat.id)) {
                      return -1;
                    }
                    return 1;
                  })
                  .map((subcategory) => (
                    <SlCheckboxContainer key={subcategory.id}>
                      <SlCheckbox
                        checked={selectedSubcategoryIds.includes(subcategory.id)}
                        onChange={(e) => handleSubcategoryChange(e, subcategory.id)}
                        id={subcategory.id.toString()}
                      />
                      <SlCheckboxLabel
                        style={{
                          ...(getTruncateStyles(1) as React.CSSProperties)
                        }}
                        htmlFor={subcategory.id.toString()}
                        title={subcategory.name}
                      >
                        {subcategory.name}
                      </SlCheckboxLabel>
                    </SlCheckboxContainer>
                  ))}
              </div>
            )}
          />
        </Flex>
      </SlDialogContent>
      <SlDialogFooter
        sx={{
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <SlButton onClick={() => handleClearOptions()}>Clear</SlButton>
        <SlButton variant="contained" onClick={() => handleNext()} disabled={!isValid}>
          Next
        </SlButton>
      </SlDialogFooter>
    </SlDialog>
  );
};
